import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button, Divider,
  Alert, CircularProgress, InputAdornment, Link, Skeleton, Tabs, Tab,
  IconButton, Chip, Stack
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// LINE Icon component
const LineIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.105.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
  </svg>
);

const Login = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const {
    loginWithPhone,
    loginWithPassword,
    registerWithPassword,
    sendPhoneCode,
    loginWithGoogle,
    getLineLoginUrl,
    isAuthenticated
  } = useAuth();

  // 登入模式：0=密碼登入, 1=簡訊登入
  const [loginMode, setLoginMode] = useState(0);
  const [isRegisterMode, setIsRegisterMode] = useState(false);

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devCode, setDevCode] = useState('');

  const googleButtonRef = useRef(null);
  const googleInitialized = useRef(false);

  // 登入方式設定
  const [authConfig, setAuthConfig] = useState({
    phone_enabled: true,
    google_enabled: false,
    google_client_id: '',
    line_enabled: false,
    line_channel_id: ''
  });
  const [configLoading, setConfigLoading] = useState(true);

  // 載入登入方式設定
  useEffect(() => {
    const loadAuthConfig = async () => {
      try {
        const res = await api.get('/settings/auth/public');
        if (res.data.success) {
          setAuthConfig(res.data.config);
        }
      } catch (err) {
        console.error('載入登入設定失敗:', err);
      } finally {
        setConfigLoading(false);
      }
    };
    loadAuthConfig();
  }, []);

  // 如果已登入，重定向
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // 驗證碼倒數計時
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 發送驗證碼
  const handleSendCode = async () => {
    if (!phone || !/^09\d{8}$/.test(phone)) {
      setError(t('login.invalid_phone'));
      return;
    }

    setLoading(true);
    setError('');

    const result = await sendPhoneCode(phone);

    if (result.success) {
      setCodeSent(true);
      setCountdown(60);
      if (result.devCode) {
        setDevCode(result.devCode);
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // 密碼登入
  const handlePasswordLogin = async () => {
    if (!phone || !/^09\d{8}$/.test(phone)) {
      setError(t('login.invalid_phone'));
      return;
    }

    if (!password || password.length < 6) {
      setError(t('login.invalid_password'));
      return;
    }

    setLoading(true);
    setError('');

    const result = await loginWithPassword(phone, password);

    if (result.success) {
      // GA4 login event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'login',
        method: 'password'
      });

      // Facebook Pixel
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'Login', { method: 'password' });
      }

      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // 註冊
  const handleRegister = async () => {
    if (!phone || !/^09\d{8}$/.test(phone)) {
      setError(t('login.invalid_phone'));
      return;
    }

    if (!password || password.length < 6) {
      setError(t('login.invalid_password'));
      return;
    }

    setLoading(true);
    setError('');

    const result = await registerWithPassword(phone, password, name);

    if (result.success) {
      // GA4 sign_up event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'sign_up',
        method: 'password'
      });

      // Facebook Pixel CompleteRegistration
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'CompleteRegistration', { method: 'password' });
      }

      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // 手機登入 (簡訊驗證碼)
  const handlePhoneLogin = async () => {
    if (!verificationCode || verificationCode.length !== 4) {
      setError(t('login.invalid_code'));
      return;
    }

    setLoading(true);
    setError('');

    const result = await loginWithPhone(phone, verificationCode);

    if (result.success) {
      // GA4 login event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'login',
        method: 'phone_sms'
      });

      // Facebook Pixel
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'Login', { method: 'phone_sms' });
      }

      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Google 登入 callback
  const handleGoogleCredential = useCallback(async (response) => {
    if (!response.credential) return;
    setLoading(true);
    setError('');
    try {
      const result = await loginWithGoogle(response.credential);
      if (result.success) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'login', method: 'google' });
        if (typeof window.fbq === 'function') {
          window.fbq('trackCustom', 'Login', { method: 'google' });
        }
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(t('login.google_login_failed'));
    } finally {
      setLoading(false);
    }
  }, [loginWithGoogle, navigate, location, t]);

  // Initialize Google Identity Services with hidden renderButton (popup OAuth flow)
  useEffect(() => {
    if (!authConfig.google_enabled || !authConfig.google_client_id || googleInitialized.current) return;

    const initializeGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: authConfig.google_client_id,
          callback: handleGoogleCredential,
          auto_select: false,
          ux_mode: 'popup',
        });
        googleInitialized.current = true;

        if (googleButtonRef.current) {
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            width: 300,
          });
        }
      }
    };

    if (window.google?.accounts?.id) {
      initializeGoogle();
      return;
    }

    const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.head.appendChild(script);
    } else {
      existingScript.addEventListener('load', initializeGoogle);
    }
  }, [authConfig.google_enabled, authConfig.google_client_id, handleGoogleCredential]);

  const handleGoogleLogin = () => {
    const btn = googleButtonRef.current?.querySelector('[role="button"]');
    if (btn) {
      btn.click();
    } else {
      setError(t('login.google_not_ready'));
    }
  };

  // LINE 登入
  const handleLineLogin = () => {
    if (!authConfig.line_enabled) {
      setError(t('login.line_not_enabled'));
      return;
    }

    // GA4 login_attempt event (before redirect)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'login_attempt',
      method: 'line'
    });

    const lineUrl = getLineLoginUrl(authConfig.line_channel_id);
    window.location.href = lineUrl;
  };

  // 檢查是否有任何社群登入啟用
  const hasSocialLogin = authConfig.google_enabled || authConfig.line_enabled;
  const surfaceSx = {
    borderRadius: 4,
    border: '1px solid rgba(16,42,67,0.1)',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #FBFCFD 100%)',
    boxShadow: '0 18px 42px rgba(16,42,67,0.08)',
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #F5F7FA 0%, #EEF4F2 100%)', py: { xs: 4, md: 7 } }}>
      <Container maxWidth="sm">
      <Paper sx={{ p: { xs: 2.5, md: 4 }, ...surfaceSx }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight={900} gutterBottom sx={{ color: '#102A43' }}>
            {t('login.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#486581', lineHeight: 1.8 }}>
            {t('login.subtitle')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip label={t('login.password_login')} size="small" sx={{ bgcolor: 'rgba(15,118,110,0.09)', color: '#0F766E', fontWeight: 700 }} />
            <Chip label={t('login.sms_login')} size="small" sx={{ bgcolor: 'rgba(15,118,110,0.09)', color: '#0F766E', fontWeight: 700 }} />
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* 社群登入按鈕 */}
        {configLoading ? (
          <Box sx={{ mb: 3 }}>
            <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
          </Box>
        ) : hasSocialLogin && (
          <Box sx={{ mb: 3 }}>
            {authConfig.google_enabled && (
              <>
                {/* Hidden Google renderButton (triggers popup OAuth) */}
                <Box ref={googleButtonRef} sx={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, overflow: 'hidden' }} />
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  sx={{
                    mb: authConfig.line_enabled ? 2 : 0,
                    py: 1.2,
                    borderColor: '#4285f4',
                    color: '#4285f4',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    borderRadius: 2.5,
                    '&:hover': { borderColor: '#357ae8', bgcolor: '#f5f9ff' }
                  }}
                  startIcon={
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  }
                >
                  {t('login.google_login')}
                </Button>
              </>
            )}

            {authConfig.line_enabled && (
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<LineIcon />}
                onClick={handleLineLogin}
                disabled={loading}
                sx={{
                  bgcolor: '#00B900',
                  borderRadius: 2.5,
                  '&:hover': { bgcolor: '#00a000' }
                }}
              >
                {t('login.line_login')}
              </Button>
            )}
          </Box>
        )}

        {hasSocialLogin && authConfig.phone_enabled && (
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">{t('login.or')}</Typography>
          </Divider>
        )}

        {/* 手機登入 */}
        {authConfig.phone_enabled && (
          <Box>
            {/* 登入方式切換 */}
            <Tabs
              value={loginMode}
              onChange={(e, v) => {
                setLoginMode(v);
                setError('');
                setCodeSent(false);
              }}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label={t('login.password_login')} icon={<LockIcon />} iconPosition="start" />
              <Tab label={t('login.sms_login')} icon={<SendIcon />} iconPosition="start" />
            </Tabs>

            {/* 密碼登入 */}
            {loginMode === 0 && (
              <Box>
                <TextField
                  fullWidth
                  label={t('login.phone')}
                  placeholder="09xxxxxxxx"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2 }}
                />

                {isRegisterMode && (
                  <TextField
                    fullWidth
                    label={t('register.name')}
                    placeholder={t('register.name_placeholder')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                )}

                <TextField
                  fullWidth
                  label={t('login.password')}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('login.password_placeholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2 }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={isRegisterMode ? handleRegister : handlePasswordLogin}
                  disabled={loading || !phone || !password}
                  sx={{ mb: 1, py: 1.2, borderRadius: 2.5 }}
                >
                  {loading ? <CircularProgress size={24} /> : (isRegisterMode ? t('login.register') : t('login.submit'))}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setIsRegisterMode(!isRegisterMode);
                      setError('');
                    }}
                  >
                    {isRegisterMode ? t('register.already_have_account') : t('register.no_account')}
                  </Button>
                </Box>
              </Box>
            )}

            {/* 簡訊登入 */}
            {loginMode === 1 && (
              <Box>
                <TextField
                  fullWidth
                  label={t('login.phone')}
                  placeholder="09xxxxxxxx"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  disabled={codeSent && countdown > 0}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2 }}
                />

                {!codeSent ? (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleSendCode}
                    disabled={loading || !phone}
                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    sx={{ py: 1.2, borderRadius: 2.5 }}
                  >
                    {loading ? t('login.sending_code') : t('login.send_code')}
                  </Button>
                ) : (
                  <Box>
                    <TextField
                      fullWidth
                      label={t('login.verify_code')}
                      placeholder={t('login.verify_code_placeholder')}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: countdown > 0 && (
                          <InputAdornment position="end">
                            <Typography variant="body2" color="text.secondary">
                              {countdown}s
                            </Typography>
                          </InputAdornment>
                        )
                      }}
                    />

                    {devCode && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {t('login.dev_code_hint', { code: devCode })}
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={handleSendCode}
                        disabled={countdown > 0 || loading}
                        sx={{ flex: 1, borderRadius: 2.5 }}
                      >
                        {countdown > 0 ? t('login.resend_countdown', { seconds: countdown }) : t('login.resend_code')}
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handlePhoneLogin}
                        disabled={loading || verificationCode.length !== 4}
                        sx={{ flex: 1, borderRadius: 2.5 }}
                      >
                        {loading ? <CircularProgress size={24} /> : t('login.submit')}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* 條款說明 */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
          {t('login.terms_agree')}{' '}
          <Link href="/shopping-guide" underline="hover">{t('login.terms_link')}</Link>
          {' '}&{' '}
          <Link href="/shopping-guide" underline="hover">{t('login.privacy_link')}</Link>
        </Typography>
      </Paper>

      {/* 購物說明連結 */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('login.first_time')}{' '}
          <Link href="/shopping-guide" underline="hover" fontWeight="bold">
            {t('login.shopping_guide')}
          </Link>
        </Typography>
      </Box>
    </Container>
    </Box>
  );
};

export default Login;
